import { useEffect, useState } from "react";
import { Envelope, Lock, BellRinging } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import {
  PREF_ALWAYS_ON,
  PREF_KEYS,
  getPreferences,
  upsertPreferences,
} from "@/lib/notifications";
import PushToggle from "@/components/parent/PushToggle";

/**
 * Parent · /parent/preferences — tiny page with four toggles for
 * notification kinds. Two of them ('incidents', 'autopay_failures')
 * are safety/billing critical and are shown as locked (always on).
 */
export default function ParentPreferences() {
  const { profile } = useAuth();
  const [prefs, setPrefs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!profile?.id) return;
    (async () => {
      try {
        const p = await getPreferences(profile.id);
        setPrefs(p);
      } catch (err) {
        toast.error(err.message || "Could not load preferences");
      } finally {
        setLoading(false);
      }
    })();
  }, [profile?.id]);

  const onToggle = async (key) => {
    if (PREF_ALWAYS_ON.has(key) || !prefs) return;
    const next = { ...prefs, [key]: !prefs[key] };
    setPrefs(next);
    setSaving(true);
    try {
      await upsertPreferences(profile.id, { [key]: next[key] });
      toast.success("Preferences saved");
    } catch (err) {
      toast.error(err.message || "Could not save");
      setPrefs(prefs); // rollback
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="h-8 w-8 rounded-full border-2 border-primary/30 border-t-primary animate-spin" />
      </div>
    );
  }

  // Belt: if a transient error made prefs null, render with defaults so
  // the UI never white-screens. The caller already showed a toast.
  const p = prefs || {
    daily_reports: true,
    incidents: true,
    invoices: true,
    autopay_failures: true,
    event_confirmations: true,
    event_reminders: true,
    weekly_photo_recap: true,
    new_message_emails: true,
    new_message_push: true,
  };

  return (
    <div data-testid="parent-preferences" className="space-y-6">
      <header>
        <p className="text-xs font-bold uppercase tracking-[0.15em] text-neutral-500">
          Account
        </p>
        <h1 className="mt-1 font-heading text-3xl font-semibold tracking-tight text-neutral-900 sm:text-4xl">
          Email preferences
        </h1>
        <p className="mt-2 max-w-2xl text-neutral-600">
          Choose which emails you want to receive from your center. Incidents
          and autopay failures are always on — we need to reach you when
          something safety- or billing-critical happens.
        </p>
      </header>

      <section className="rounded-2xl border border-neutral-200 bg-white p-5">
        <p className="mb-4 flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.15em] text-neutral-500">
          <BellRinging size={14} weight="duotone" className="text-primary-hover" />
          Push notifications
        </p>
        <PushToggle
          prefOn={!!p.new_message_push}
          onPrefChange={async (on) => {
            const next = { ...p, new_message_push: on };
            setPrefs(next);
            try {
              await upsertPreferences(profile.id, { new_message_push: on });
            } catch (err) {
              setPrefs(prefs);
              throw err;
            }
          }}
          disabled={saving}
        />
      </section>

      <section className="rounded-2xl border border-neutral-200 bg-white p-5">
        <p className="mb-4 flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.15em] text-neutral-500">
          <Envelope size={14} weight="duotone" className="text-primary-hover" />
          Transactional emails
        </p>
        <ul data-testid="parent-preferences-list" className="space-y-3">
          {PREF_KEYS.filter((pk) => pk.key !== "new_message_push").map(({ key, label, desc }) => {
            const locked = PREF_ALWAYS_ON.has(key);
            const on = locked ? true : !!p[key];
            return (
              <li
                key={key}
                data-testid={`parent-preferences-item-${key}`}
                data-enabled={on}
                data-locked={locked}
                className="flex items-start justify-between gap-4 rounded-2xl border border-neutral-200 p-4"
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5">
                    <p className="font-heading text-sm font-semibold text-neutral-900">{label}</p>
                    {locked && <Lock size={11} weight="duotone" className="text-neutral-400" />}
                  </div>
                  <p className="mt-0.5 text-xs leading-relaxed text-neutral-500">{desc}</p>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={on}
                  aria-label={`Toggle ${label}`}
                  data-testid={`parent-preferences-toggle-${key}`}
                  disabled={locked || saving}
                  onClick={() => onToggle(key)}
                  className={`relative mt-0.5 inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full border transition-all ${
                    on ? "border-primary/50 bg-primary" : "border-neutral-200 bg-neutral-200"
                  } ${locked ? "cursor-not-allowed opacity-60" : ""}`}
                >
                  <span
                    className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-sm transition-transform ${
                      on ? "translate-x-5" : "translate-x-0.5"
                    }`}
                  />
                </button>
              </li>
            );
          })}
        </ul>
      </section>
    </div>
  );
}
